import { useRef } from "react";
import classNames from "classnames";
import { isSameDay } from "date-fns";
import { noop } from "lodash";
import DatePicker from "react-datepicker";
import type { ReactDatePickerProps } from "react-datepicker";
import { Controller, useFormContext } from "react-hook-form";
import {
  getNextQuarter,
  isMidnight,
} from "@circle-react/helpers/dateTimeHelpers";
import { Icon } from "@circle-react-shared/Icon";
import { Typography } from "../Typography";
import { getMinAndMaxTime } from "./getMinAndMaxTime";
import "./datepicker.scss";

export interface DateTimePickerProps
  extends Omit<ReactDatePickerProps, "onChange"> {
  className?: string;
  classNameDate?: string;
  classNameTime?: string;
  dataTestId?: string;
  datePlaceholder?: string;
  disabled?: boolean;
  labelDate?: string;
  labelTime?: string;
  maxDate?: Date;
  minDate?: Date;
  name: string;
  onChange?: (date: Date) => void;
  timePlaceholder?: string;
}

export const DateTimePicker = ({
  className = "",
  classNameDate = "",
  classNameTime = "",
  dataTestId,
  datePlaceholder = "",
  disabled = false,
  labelDate = "",
  labelTime = "",
  maxDate,
  minDate,
  name,
  onChange = noop,
  timePlaceholder = "",
  ...rest
}: DateTimePickerProps) => {
  const { control } = useFormContext();

  const refDate = useRef<any>();
  const refTime = useRef<any>();

  const handleDateChange =
    (setFormValue = noop) =>
    (value?: Date | null) => {
      if (!(value instanceof Date)) return;
      let scheduledDateTime = value;

      if (
        minDate &&
        isSameDay(scheduledDateTime, minDate) &&
        isMidnight(scheduledDateTime)
      ) {
        scheduledDateTime = getNextQuarter(minDate);
      }

      setFormValue(scheduledDateTime.toUTCString());

      onChange(scheduledDateTime);
    };

  return (
    <Controller
      control={control}
      name={name}
      render={({
        field: { onChange, value },
      }: {
        field: { onChange: (date: Date) => void; value: Date };
      }) => {
        const selectedDate = value && new Date(value);

        const { minTime, maxTime } = getMinAndMaxTime(
          selectedDate,
          minDate,
          maxDate,
        );

        return (
          <div
            className={classNames("flex", className)}
            data-testid={dataTestId}
          >
            <div className={classNameDate}>
              {labelDate && (
                <label htmlFor={name} className="mb-2">
                  <Typography.LabelSm weight="semibold">
                    {labelDate}
                  </Typography.LabelSm>
                </label>
              )}
              <div className="relative mr-1" data-testid="event_date">
                <DatePicker
                  ref={refDate}
                  selected={selectedDate}
                  dateFormat="MMM d, yyyy"
                  onChange={handleDateChange(onChange)}
                  minDate={minDate}
                  maxDate={maxDate}
                  name={name}
                  disabled={disabled}
                  placeholderText={datePlaceholder}
                  className="relative"
                  {...rest}
                />
                <Icon
                  type="cheveron-down"
                  className="absolute right-4 top-1/2 -translate-y-1/2 cursor-pointer hover:text-gray-800"
                  onClick={() => {
                    refDate.current?.input?.focus();
                  }}
                />
              </div>
            </div>
            <div className={classNameTime}>
              {labelTime && (
                <label htmlFor={name} className="mb-2">
                  <Typography.LabelSm weight="semibold">
                    {labelTime}
                  </Typography.LabelSm>
                </label>
              )}
              <div className="relative ml-1" data-testid="event_hour">
                <DatePicker
                  ref={refTime}
                  selected={selectedDate}
                  onChange={handleDateChange(onChange)}
                  showTimeSelect
                  showTimeSelectOnly
                  timeIntervals={15}
                  dateFormat="h:mm aa"
                  disabled={disabled}
                  placeholderText={timePlaceholder}
                  minTime={minTime}
                  maxTime={maxTime}
                  className="relative"
                  {...rest}
                />
                <Icon
                  type="cheveron-down"
                  className="absolute right-4 top-1/2 -translate-y-1/2 cursor-pointer hover:text-gray-800"
                  onClick={() => {
                    refTime.current?.input?.focus();
                  }}
                />
              </div>
            </div>
          </div>
        );
      }}
    />
  );
};
